import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { translations, LANGUAGES, DEFAULT_LANG } from "@/i18n/translations";

const LanguageContext = createContext(null);

function resolveInitialLang() {
  // 1) explicit stored choice wins, 2) browser language if supported,
  // 3) Italian as the guaranteed default.
  const stored = localStorage.getItem("avenzu_lang");
  if (stored && translations[stored]) return stored;
  const nav = (navigator.language || navigator.userLanguage || "").slice(0, 2).toLowerCase();
  if (translations[nav]) return nav;
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(resolveInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => {
    if (!translations[l]) return;
    localStorage.setItem("avenzu_lang", l);
    setLangState(l);
  }, []);

  const t = useCallback(
    (key) => {
      const read = (dict) => key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), dict);
      const val = read(translations[lang]);
      if (val !== undefined) return val;
      const fallback = read(translations.en);
      return fallback !== undefined ? fallback : key;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  return useContext(LanguageContext);
}
